/**
 * Role Definitions
 *
 * Single source of truth for role constants and role hierarchy.
 */

// =============================================================================
// ROLE CONSTANTS
// =============================================================================

export const ROLES = {
  OWNER: "owner",
  ADMIN: "admin",
  MANAGER: "manager",
  USER: "user",
  VIEWER: "viewer",
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

// =============================================================================
// ROLE HIERARCHY
// =============================================================================

/**
 * Role hierarchy - higher index = more permissions.
 * Used for role comparison (e.g., isAtLeast).
 */
const ROLE_HIERARCHY: Role[] = [
  ROLES.VIEWER,
  ROLES.USER,
  ROLES.MANAGER,
  ROLES.ADMIN,
  ROLES.OWNER,
];

/**
 * Get the hierarchy level of a role (higher = more permissions).
 */
export function getRoleLevel(role: string): number {
  const index = ROLE_HIERARCHY.indexOf(role as Role);
  return index === -1 ? -1 : index;
}

/**
 * Check if roleA is at least as privileged as roleB.
 */
export function isRoleAtLeast(roleA: string, roleB: string): boolean {
  return getRoleLevel(roleA) >= getRoleLevel(roleB);
}

// =============================================================================
// ROLE DISPLAY
// =============================================================================

const ROLE_LABELS: Record<Role, string> = {
  [ROLES.OWNER]: "Owner",
  [ROLES.ADMIN]: "Administrator",
  [ROLES.MANAGER]: "Manager",
  [ROLES.USER]: "User",
  [ROLES.VIEWER]: "Viewer",
};

/**
 * Get a human-readable label for a role.
 */
export function getRoleLabel(role: string): string {
  return ROLE_LABELS[role as Role] ?? role;
}
